import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export type DropdownOption<T = string> = {
    label: string;
    value: T;
};

type SelectDropdownProps<T = string> = {
    value: T;
    onChange: (val: T) => void;
    options: DropdownOption<T>[];
    placeholder?: string;
    className?: string;
};

export default function SelectDropdown<T = string>({
    value,
    onChange,
    options,
    placeholder = 'Select...',
    className = 'w-[200px]',
}: SelectDropdownProps<T>) {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedLabel =
        options.find((opt) => opt.value === value)?.label || placeholder;

    return (
        <div className={`relative inline-block ${className}`} ref={dropdownRef}>
            <button
                onClick={() => setOpen(!open)}
                className="relative w-full px-4 py-3 pr-10 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm flex items-center justify-between focus:outline-none focus:ring-0 focus:border-gray-400"
            >
                {selectedLabel}
                <ChevronDown className="absolute right-3 w-4 h-4 text-gray-400 pointer-events-none" />
            </button>

            {open && (
                <div className="absolute mt-1 z-10 w-full bg-white shadow-md border border-gray-800 rounded-md overflow-hidden text-sm">
                    {options.map((option) => {
                        const isSelected = value === option.value;
                        return (
                            <div
                                key={String(option.value)}
                                onClick={() => {
                                    onChange(option.value);
                                    setOpen(false);
                                }}
                                className={`cursor-pointer px-4 py-2 flex items-center gap-2 hover:bg-gray-100 ${isSelected ? 'bg-gray-50 font-medium' : ''
                                    }`}
                            >
                                <div className="w-4">
                                    {isSelected && <Check className="w-4 h-4 text-black" />}
                                </div>
                                <span>{option.label}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}